import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { useUserStore } from "@/store/modules/user";

import { useMessage } from "@/composables/useMessage";
const message = useMessage();

/**
 * 创建axios实例
 */
const createAxiosInstance = (): AxiosInstance => {
  const baseURL = import.meta.env.DEV ? "/api" : import.meta.env.VITE_API_BASE_URL;
  
  const instance = axios.create({
    baseURL: baseURL,
    timeout: 10* 60 * 1000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  /**
   * 请求拦截器
   */
  instance.interceptors.request.use(
    (config) => {
      const userStore = useUserStore();

      // 添加token到请求头
      if (userStore.token) {
        config.headers.token = userStore.token;
      }

      // // 开发环境下打印请求信息
      // if (import.meta.env.DEV) {
      //   console.log("🚀 API Request:", {
      //     url: config.url,
      //     method: config.method,
      //     data: config.data,
      //   });
      // }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  /**
   * 响应拦截器
   */
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const { data } = response;
      const userStore = useUserStore();

      // // 开发环境下打印响应信息
      // if (import.meta.env.DEV) {
      //   console.log("✅ API Response:", {
      //     url: response.config.url,
      //     status: response.status,
      //     data: data,
      //   });
      // }

      const location = window.location;
      const isLoginOrHome = ["/login", "/", "/auto-login"].includes(location.pathname);
      if (data.code === 20001 && !isLoginOrHome) {
        message.error("登录已过期，请重新登录");
        userStore.logout();
        setTimeout(() => {
          window.location.href = "/app/dashboard";
        }, 1000);
        return;
      }

      // 处理业务逻辑错误
      if (data.code !== 1) {
        if (data.code !== 1002) {
          message.error(data.msg || "请求失败");
        }
        return Promise.reject(data);
      }

      return data;
    },
    (error) => {
      // 处理HTTP错误
      if (error.code === "ERR_NETWORK") {
        message.error(error.message);
        return;
      } else if (error.code === "ECONNABORTED") {
        message.error("请求超时");
        return;
      }
      const { status, data } = error.response || {};

      switch (status) {
        case 401:
          message.error("登录已过期，请重新登录");
          const userStore = useUserStore();
          userStore.logout();
          break;
        case 403:
          message.error("没有权限访问该资源");
          break;
        case 404:
          message.error("请求的资源不存在");
          break;
        case 500:
          message.error("服务器内部错误");
          break;
        default:
          message.error(data.msg || "网络错误，请稍后重试");
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * 导出axios实例
 */
export const request = createAxiosInstance();

/**
 * 通用请求方法
 */
export const http = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return request.get(url, config);
  },

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return request.post(url, data, config);
  },

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return request.put(url, data, config);
  },

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return request.delete(url, config);
  },
};

export default request;
